import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiClock, FiSend, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useScrollAnim, V } from '../hooks/useScrollAnimation';
import { submitInquiry } from '../api/axios';

const CONTACTS = [
  { 
    Icon: FiPhone, 
    label: 'Business Phone', 
    value: '+91 98219 91932', 
    link: 'tel:+919821991932', 
    color: '#E91E8C' 
  },
  { 
    Icon: FiMail, 
    label: 'Business Email', 
    value: 'info.shreeparasnathenterprises@gmail.com', 
    link: 'mailto:info.shreeparasnathenterprises@gmail.com', 
    color: '#FF6B2B' 
  },
  { 
    Icon: FaWhatsapp, 
    label: 'WhatsApp Support', 
    value: 'Instant Business Response', 
    link: 'https://wa.me/919821991932', 
    color: '#25D366' 
  },
];

const SUPPORT_AREAS = [
  "Packaging Solutions",
  "Procurement Support",
  "Distribution Services",
  "Business Partnerships",
  "Custom Requirements"
];

const REQUIREMENT_TYPES = [
  "Packaging Solutions",
  "Procurement Support",
  "Distribution Services",
  "Business Partnership",
  "Custom Requirement",
  "Other"
];

export default function Contact() {
  const { ref, controls } = useScrollAnim(0.08);
  
  const [form, setForm] = useState({
    name: '',
    company: '',
    industry: '',
    requirementType: '',
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [focused, setFocused] = useState('');

  const onChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.company || !form.message) {
      setError('Please fill Name, Company and Message fields.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const res = await submitInquiry({
        ...form,
        phone: '', // optional
        productInterest: form.requirementType
      });
      setSuccess(res.data.message || 'Thank you! Our team will contact you shortly.');
      setForm({ name: '', company: '', industry: '', requirementType: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" style={{
      padding: 'var(--spy) 0',
      background: 'linear-gradient(170deg,#FFF8EE 0%,#FFF5F9 50%,#fff 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none' }} />

      <div className="container" ref={ref}>
        {/* Header */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={V.stagger}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <motion.span
            variants={V.child}
            style={{
              display: 'block',
              fontFamily: 'var(--font-body)',
              fontSize: '0.78rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              marginBottom: '0.8rem'
            }}
          >
            CONTACT SPE
          </motion.span>

          <motion.h2
            variants={V.child}
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(2.3rem, 5vw, 3.5rem)',
              color: 'var(--text)',
              lineHeight: 1.1,
              marginBottom: '1.1rem'
            }}
          >
            Connect With{' '}
            <span style={{
              background: 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Our Team
            </span>
          </motion.h2>

          <motion.p
            variants={V.child}
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              fontSize: '1.05rem',
              maxWidth: 620,
              margin: '0 auto',
              lineHeight: 1.7
            }}
          >
            Discuss packaging solutions, procurement requirements, distribution support, or business opportunities with SPE.
          </motion.p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 'clamp(2.5rem, 5vw, 4rem)',
          alignItems: 'start'
        }}>

          {/* LEFT COLUMN - Contact Info */}
          <motion.div
            initial="hidden"
            animate={controls}
            variants={V.fadeLeft}
            style={{ display: 'flex', flexDirection: 'column', gap: '1.8rem' }}
          >
            {CONTACTS.map(({ Icon, label, value, link, color }) => (
              <motion.a
                key={label}
                href={link}
                target={label.includes('WhatsApp') ? '_blank' : undefined}
                rel="noreferrer"
                whileHover={{ x: 8 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.2rem',
                  padding: '1.4rem 1.6rem',
                  background: '#fff',
                  borderRadius: 20,
                  border: '1.5px solid rgba(233,30,140,0.1)',
                  boxShadow: 'var(--shadow-card)',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: `${color}12`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color,
                  flexShrink: 0
                }}>
                  <Icon size={26} />
                </div>
                <div>
                  <p style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '0.2rem'
                  }}>
                    {label}
                  </p>
                  <p style={{ fontWeight: 600, color: 'var(--text)' }}>{value}</p>
                </div>
              </motion.a>
            ))}

            {/* Business Hours */}
            <div style={{
              background: '#fff',
              borderRadius: 20,
              padding: '1.6rem',
              border: '1.5px solid rgba(233,30,140,0.1)',
              boxShadow: 'var(--shadow-card)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem' }}>
                <FiClock size={24} style={{ color: '#E91E8C' }} />
                <p style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '1.05rem' }}>
                  Business Hours
                </p>
              </div>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
                Monday – Saturday<br />
                9:00 AM – 7:00 PM IST
              </p>
            </div>

            {/* Serving India */}
            <div style={{
              background: '#fff',
              borderRadius: 20,
              padding: '1.6rem',
              border: '1.5px solid rgba(233,30,140,0.1)',
              boxShadow: 'var(--shadow-card)',
              textAlign: 'center'
            }}>
              <p style={{ fontWeight: 700, color: '#E91E8C', marginBottom: '0.4rem' }}>
                Serving Clients Across India
              </p>
              <p style={{ color: 'var(--text-muted)' }}>
                PAN India distribution and support
              </p>
            </div>

            {/* Big WhatsApp Button */}
            <motion.a
              href="https://wa.me/919821991932?text=Hi SPE Team, I would like to discuss business opportunities."
              target="_blank"
              rel="noreferrer"
              whileHover={{ scale: 1.03 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '1.2rem',
                borderRadius: 16,
                background: 'linear-gradient(135deg,#25D366,#128C7E)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.02rem',
                textDecoration: 'none',
                boxShadow: '0 10px 30px rgba(37,211,102,0.35)'
              }}
            >
              <FaWhatsapp size={22} /> Connect Via WhatsApp
            </motion.a>
          </motion.div>

          {/* RIGHT COLUMN - Business Inquiry Form */}
          <motion.div
            initial="hidden"
            animate={controls}
            variants={V.fadeRight}
            style={{
              background: '#fff',
              borderRadius: 28,
              padding: 'clamp(2rem, 5vw, 3rem)',
              border: '1.5px solid rgba(233,30,140,0.1)',
              boxShadow: '0 12px 50px rgba(233,30,140,0.1)'
            }}
          >
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: '1.75rem',
              marginBottom: '0.4rem'
            }}>
              Business Inquiry Form
            </h3>
            <p style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--text-muted)',
              marginBottom: '2rem'
            }}>
              Share your requirements and our team will get back to you with the appropriate solution.
            </p>

            {success && (
              <div style={{
                display: 'flex', gap: '12px', padding: '1rem',
                background: 'rgba(34,197,94,0.1)', borderRadius: 12,
                border: '1px solid rgba(34,197,94,0.3)', marginBottom: '1.5rem'
              }}>
                <FiCheckCircle size={20} style={{ color: '#22C55E', marginTop: 2 }} />
                <p style={{ color: '#15803D' }}>{success}</p>
              </div>
            )}

            {error && (
              <div style={{
                display: 'flex', gap: '12px', padding: '1rem',
                background: 'rgba(239,68,68,0.1)', borderRadius: 12,
                border: '1px solid rgba(239,68,68,0.3)', marginBottom: '1.5rem'
              }}>
                <FiAlertCircle size={20} style={{ color: '#EF4444', marginTop: 2 }} />
                <p style={{ color: '#B91C1C' }}>{error}</p>
              </div>
            )}

            <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              <input
                name="name"
                placeholder="Your Full Name *"
                value={form.name}
                onChange={onChange}
                onFocus={() => setFocused('name')}
                onBlur={() => setFocused('')}
                style={{ width: '100%', padding: '1rem', borderRadius: 12, border: `1.5px solid ${focused === 'name' ? '#E91E8C' : 'rgba(233,30,140,0.2)'}` }}
              />

              <input
                name="company"
                placeholder="Company / Business Name *"
                value={form.company}
                onChange={onChange}
                style={{ width: '100%', padding: '1rem', borderRadius: 12, border: '1.5px solid rgba(233,30,140,0.2)' }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <input
                  name="industry"
                  placeholder="Industry"
                  value={form.industry}
                  onChange={onChange}
                  style={{ width: '100%', padding: '1rem', borderRadius: 12, border: '1.5px solid rgba(233,30,140,0.2)' }}
                />

                <select
                  name="requirementType"
                  value={form.requirementType}
                  onChange={onChange}
                  style={{ width: '100%', padding: '1rem', borderRadius: 12, border: '1.5px solid rgba(233,30,140,0.2)', background: '#fff' }}
                >
                  <option value="">Requirement Type</option>
                  {REQUIREMENT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <textarea
                name="message"
                placeholder="Tell us about your requirements / project scope *"
                rows={5}
                value={form.message}
                onChange={onChange}
                style={{
                  width: '100%',
                  padding: '1rem',
                  borderRadius: 12,
                  border: '1.5px solid rgba(233,30,140,0.2)',
                  resize: 'vertical',
                  fontFamily: 'var(--font-body)'
                }}
              />

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={!loading ? { scale: 1.02 } : {}}
                style={{
                  padding: '1.1rem',
                  borderRadius: 14,
                  border: 'none',
                  background: loading ? '#ccc' : 'linear-gradient(135deg,#E91E8C,#FF6B2B)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1.02rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  marginTop: '0.5rem'
                }}
              >
                <FiSend style={{ marginRight: '10px' }} />
                {loading ? 'Submitting...' : 'Submit Request'}
              </motion.button>

              <p style={{
                textAlign: 'center',
                fontSize: '0.85rem',
                color: 'var(--text-muted)',
                marginTop: '0.8rem'
              }}>
                Our team typically responds within one business day.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}